// In-memory fake RealityAgent, one per PC id. STAGING ONLY: rendered through
// components/FakePCTile when Settings -> Staging tools -> "Fake PC tiles" is
// on (utils/env.ts FAKE_PC_TILES). Lets the operator watch the queue flow
// (dispatch pop-up, visitor on the tile, nearly-done banner, recovery actions)
// without a single RealityAgent running.
//
// Module-level so the state survives tile re-renders and even remounts (room
// switch, view switch): a fake run keeps counting while the dashboard is
// away, like a real PC would. React subscribes via useSyncExternalStore.
// Nothing here talks HTTP; the only side effects are the queue RPCs the tile
// itself fires (dequeue / requeue), exactly like PCTile.
//
// Crash ladder: the fake agent walks the SAME rungs as RealityAgent's
// api/crash_recovery.go, so the staging drill shows what a real room shows.
// A crash does not free the tile: 'recovering' relaunches the same run with
// the same remaining time, and a second crash inside the window trips the
// loop guard into 'gaveup', which sticks until the operator resolves the
// visitor (recovery action) or launches something fresh.

import type { CrashRecovery } from '../types/database';

export type FakePhase = 'idle' | 'loading' | 'running' | 'done' | 'recovering' | 'gaveup';

export interface FakeRun {
  experienceId: string;
  name: string;
  version: string;
  language: string | null;
  /** When the launch was accepted (loading starts). */
  launchedAt: number;
  loadingMs: number;
  runMs: number;
  /** "Vastloper": never leaves loading until stopped (the no-injection case). */
  stuck: boolean;
  runStartedAt: number | null;
  endsAt: number | null;
  /** The tile fired the nearly-done broadcast for this run. */
  nearlyDoneFired: boolean;
}

export interface FakeAgentState {
  phase: FakePhase;
  run: FakeRun | null;
  /** Arm the NEXT launch as a stuck start (one-shot, like /api/simulation/next). */
  stuckArmed: boolean;
  /** Short note shown by the tile after a crash. */
  note: string | null;
  /** When the current phase was entered. */
  phaseAt: number;
  /** Bumped every second while not idle so elapsed timers re-render. */
  tick: number;
  /** Crash timestamps still inside FAKE_CRASH_WINDOW_MS — the loop guard.
   *  Reset by a fresh launch, a clean finish and a stop. */
  crashHistory: number[];
}

/** How long "Klaar" stays on the tile before it goes idle. */
export const FAKE_DONE_FLASH_MS = 3_000;
/** Mirrors the agent's stage-1 last_level_warning lead for the fake run. */
export const FAKE_NEARLY_DONE_LEAD_MS = 15_000;
/** Sliding loop-guard window (agent: config.CrashRecovery.RestartWindowSec). */
export const FAKE_CRASH_WINDOW_MS = 120_000;
/** How long the fake relaunch takes (agent: kill, record, relaunch). */
export const FAKE_CRASH_RESTART_MS = 5_000;
/** Restarts allowed inside the window; the next crash gives up (agent default). */
export const FAKE_MAX_RESTARTS = 1;

const IDLE: FakeAgentState = {
  phase: 'idle',
  run: null,
  stuckArmed: false,
  note: null,
  phaseAt: 0,
  tick: 0,
  crashHistory: [],
};

const agents = new Map<string, FakeAgentState>();
const listeners = new Map<string, Set<() => void>>();

export function getFakeAgent(pcId: string): FakeAgentState {
  return agents.get(pcId) ?? IDLE;
}

export function subscribeFakeAgent(pcId: string, cb: () => void): () => void {
  let set = listeners.get(pcId);
  if (!set) {
    set = new Set();
    listeners.set(pcId, set);
  }
  set.add(cb);
  return () => {
    set?.delete(cb);
  };
}

function update(pcId: string, patch: Partial<FakeAgentState>): void {
  const next = { ...getFakeAgent(pcId), ...patch };
  agents.set(pcId, next);
  ensureTicker();
  const set = listeners.get(pcId);
  if (set) for (const cb of set) cb();
}

/** True while an experience process is up (loading or running). The dispatch
 *  report's `running` is wider: it also covers 'recovering', where the process
 *  is gone but the same run is coming back. */
export function fakeAgentBusy(pcId: string): boolean {
  const p = getFakeAgent(pcId).phase;
  return p === 'loading' || p === 'running';
}

export function fakeAgentLaunch(
  pcId: string,
  opts: {
    experienceId: string;
    name: string;
    version: string;
    language: string | null;
    loadingMs: number;
    runMs: number;
  },
): void {
  const cur = getFakeAgent(pcId);
  const now = Date.now();
  update(pcId, {
    phase: 'loading',
    phaseAt: now,
    note: null,
    // A fresh launch clears the crash state, exactly like the agent does, so
    // "Opnieuw starten" on a gave-up tile does not immediately re-suppress it.
    crashHistory: [],
    // The Vastloper arm is consumed by this launch.
    stuckArmed: false,
    run: {
      experienceId: opts.experienceId,
      name: opts.name,
      version: opts.version,
      language: opts.language,
      launchedAt: now,
      loadingMs: Math.max(0, opts.loadingMs),
      runMs: Math.max(1_000, opts.runMs),
      stuck: cur.stuckArmed,
      runStartedAt: null,
      endsAt: null,
      nearlyDoneFired: false,
    },
  });
}

/** Stop whatever loads/runs — including a crash the ladder is still on, so a
 *  recovery action (or undo-with-stop) really does resolve the tile. Returns
 *  true when there was something to stop. */
export function fakeAgentStop(pcId: string): boolean {
  const phase = getFakeAgent(pcId).phase;
  if (phase === 'idle' || phase === 'done') return false;
  update(pcId, { phase: 'idle', phaseAt: Date.now(), run: null, note: null, crashHistory: [] });
  return true;
}

/** Simulate a crash (exit code 3) of the loading/running experience, and walk
 *  the agent's ladder: relaunch the same run, or give up once the loop guard
 *  trips. Returns which rung we landed on (null = nothing was running). */
export function fakeAgentCrash(pcId: string): 'restarting' | 'gave_up' | null {
  if (!fakeAgentBusy(pcId)) return null;
  const now = Date.now();
  const cur = getFakeAgent(pcId);
  const history = [
    ...cur.crashHistory.filter((t) => now - t <= FAKE_CRASH_WINDOW_MS),
    now,
  ];
  const giveUp = history.length > FAKE_MAX_RESTARTS;
  update(pcId, {
    phase: giveUp ? 'gaveup' : 'recovering',
    phaseAt: now,
    crashHistory: history,
    note: 'Gecrasht (exit 3)',
  });
  return giveUp ? 'gave_up' : 'restarting';
}

/** The fake agent's crash_recovery block, shaped exactly like the real one so
 *  FakePCTile feeds services/tileRecovery and the dispatch report the same way
 *  PCTile does. null = healthy. */
export function fakeCrashRecovery(state: FakeAgentState): CrashRecovery | null {
  if (state.phase !== 'recovering' && state.phase !== 'gaveup') return null;
  return {
    restarting: state.phase === 'recovering',
    crashes_in_window: state.crashHistory.length,
    window_seconds: FAKE_CRASH_WINDOW_MS / 1_000,
    gave_up: state.phase === 'gaveup',
    last_exit_code: 3,
    last_crash_at: new Date(state.phaseAt).toISOString(),
    experience_id: state.run?.experienceId ?? '',
    experience_name: state.run?.name ?? '',
    run_id: '',
  };
}

export function fakeAgentArmStuck(pcId: string, armed: boolean): void {
  update(pcId, { stuckArmed: armed });
}

export function fakeAgentMarkNearlyDone(pcId: string): void {
  const cur = getFakeAgent(pcId);
  if (!cur.run) return;
  update(pcId, { run: { ...cur.run, nearlyDoneFired: true } });
}

// One ticker for all fake agents: advances phases and bumps `tick` once a
// second while at least one agent is not idle. Stops itself otherwise.
let ticker: ReturnType<typeof setInterval> | null = null;

function ensureTicker(): void {
  if (ticker) return;
  let anyBusy = false;
  for (const a of agents.values()) {
    if (a.phase !== 'idle') {
      anyBusy = true;
      break;
    }
  }
  if (!anyBusy) return;
  ticker = setInterval(tickAll, 1_000);
}

function tickAll(): void {
  const now = Date.now();
  let anyBusy = false;
  for (const [pcId, a] of agents) {
    if (a.phase === 'idle') continue;
    anyBusy = true;
    const run = a.run;
    switch (a.phase) {
      case 'loading':
        if (run && !run.stuck && now >= run.launchedAt + run.loadingMs) {
          update(pcId, {
            phase: 'running',
            phaseAt: now,
            run: { ...run, runStartedAt: now, endsAt: now + run.runMs },
          });
        } else {
          update(pcId, { tick: a.tick + 1 });
        }
        break;
      case 'running':
        if (run && run.endsAt !== null && now >= run.endsAt) {
          update(pcId, { phase: 'done', phaseAt: now });
        } else {
          update(pcId, { tick: a.tick + 1 });
        }
        break;
      case 'done':
        if (now >= a.phaseAt + FAKE_DONE_FLASH_MS) {
          // A clean finish resets the loop guard, like the agent's completion
          // callback does.
          update(pcId, { phase: 'idle', phaseAt: now, run: null, crashHistory: [] });
        }
        break;
      case 'recovering':
        // Same visitor, same run, same remaining time: shift the run's clocks
        // by however long the relaunch took instead of restarting them.
        if (now >= a.phaseAt + FAKE_CRASH_RESTART_MS) {
          if (!run) {
            update(pcId, { phase: 'idle', phaseAt: now, run: null, note: null });
            break;
          }
          const shift = now - a.phaseAt;
          update(pcId, {
            phase: run.runStartedAt === null ? 'loading' : 'running',
            phaseAt: now,
            note: null,
            run: {
              ...run,
              launchedAt: run.launchedAt + shift,
              runStartedAt: run.runStartedAt === null ? null : run.runStartedAt + shift,
              endsAt: run.endsAt === null ? null : run.endsAt + shift,
            },
          });
        } else {
          update(pcId, { tick: a.tick + 1 });
        }
        break;
      case 'gaveup':
        // Sticks until the operator resolves the visitor or launches fresh.
        break;
    }
  }
  if (!anyBusy && ticker) {
    clearInterval(ticker);
    ticker = null;
  }
}
